import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { Models } from '@ibexfusion/models';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TimePickerComponent } from '../time-picker/time-picker.component';
import { TranslateService } from '@services/translate/translate.service';

import JsonPointer from 'jsonpointer';;

export interface FormEvent {
  type: string;
  value: string;
}

@Component({
    selector: 'smart-input',
    templateUrl: './smart-input.component.html',
    styleUrls: ['./smart-input.component.scss'],
    standalone: false
})
export class SmartInputComponent implements OnInit, OnChanges {
  @Input() key: string;
  @Input() keyName: string;
  @Input() model: Models.IbexBaseModel;
  @Input() jsonPointerKey: string;
  @Input() index: number;
  @Input() format: string;
  @Input() translationPrefix: string;
  @Input() hintTranslationPrefix: string;
  @Input() hasTrash = false;
  @Input() hideDescription = false;
  @Input() translationKey = null;
  @Input() fieldConfig: any;
  @Input() height: any;
  @Input() forceDisplay: string;
  @Output() onChange = new EventEmitter<any>();
  @Output() modelChange = new EventEmitter<any>();

  displayEnum = Models.DisplayTypes;
  typeEnum = Models.PropertyTypes;
  property: Models.PropertyValue;
  errorMessages: Models.ValidationResult[] = [];
  isValid: boolean;
  dropdownElements: string[];
  arrayElements: any[];
  translations: any;
  private currentLanguage = 'de' // currently only german

  constructor(private dialog: MatDialog, private translate: TranslateService) {
  }

  async ngOnInit() {
    this.translations = this.translate.get(this.translationPrefix || this.translationKey) || {};
    this.init();
  }

  init() {
    this.keyName = this.keyName || this.key;
    if (this.jsonPointerKey && JsonPointer.get(this.model, this.jsonPointerKey) && JsonPointer.get(this.model, this.jsonPointerKey).hasOwnProperty(this.keyName)) {
      // with jsonPointerKey you can access the submodel where the key is placed: use /sub1/sub2 and so on
      this.model = JsonPointer.get(this.model, this.jsonPointerKey);
    }
    this.property = this.model?.getReflection?.()?.getProperties?.()?.[this.keyName];
    if (!this.property || !this.model) {
      if (this.fieldConfig) {
        this.property = {
          type: Models.PropertyTypes[this.fieldConfig?.type], displayAs: Models.DisplayTypes[this.fieldConfig?.displayAs], validators: this.fieldConfig?.validators
        } as any;
      } else {
        return;
      }
    }
    this.property.displayAs = (this.displayEnum[this.forceDisplay] ?? this.property.displayAs) as any;
    if (this.property?.displayAs === Models.DisplayTypes.ARRAY) {
      if (this.property?.type === Models.PropertyTypes.STRING) {
        if (this.model?.[this.keyName]) {
          try {
            this.arrayElements = this.model[this.keyName].split(this.property.separator);
          } catch (err) {
            console.log('Catched error: ', err, this.model[this.keyName])
          }
        } else {
          this.arrayElements = [];
        }
      } else if (this.property.type === Models.PropertyTypes.ARRAY) {
        this.arrayElements = this.model[this.keyName];
      }
    }
    this.getDropdownElements();
    this.check();
  }

  ngOnChanges() {
    this.init();
  }

  public reloadValue() {
    const key = JSON.parse(JSON.stringify(this.key))
    this.key = null;
    setTimeout(() => { this.key = key }, 0);
  }

  change(event) {
    this.onChange.emit(event?.value ?? event?.checked ?? event?.target?.value ?? event?.currentTarget?.target?.value ?? event);
  }

  clearField() {
    this.model[this.key] = null;
  }

  quillChanged(event) {
    this.model[this.key] = event;
    this.change(event);
  }

  getDropdownElements() {
    if (this.property.displayAs === this.displayEnum.DROPDOWN) {
      this.dropdownElements = this.property.validators.find(v => !!v?.string?.listValues)?.string.listValues;
      if (!this.dropdownElements) {
        console.error('no dropdown values defined for ' + this.key);
        throw new Error('no dropdown values defined for ' + this.key);
      }
    }
  }

  async check() {
    this.errorMessages = [];
    if (this.model?.validateField && !this.fieldConfig) {
      const validationResult = await this.model.validateField(this.key)
      this.isValid = validationResult.success;
      this.errorMessages = validationResult?.results ? validationResult.results.filter(errormessage => !errormessage.success) : (validationResult.message ? [validationResult] : []);
    }
  }

  addElement(event: any) {
    if (event.target.value !== '') {
      if (!this.arrayElements) {
        this.arrayElements = [];
      }
      this.arrayElements.push(event.target.value);
      event.target.value = '';
      this.rebuildArray();
    }
    this.change(event);
  }

  removeElement(index: number) {
    this.arrayElements.splice(index, 1);
    this.rebuildArray();
  }

  rebuildArray() {
    if (this.model.getReflection().getProperties()[this.key].type === Models.PropertyTypes.STRING) {
      this.model[this.key] = this.arrayElements.join(this.model.getReflection().getProperties()[this.key].separator);
    } else if (this.model.getReflection().getProperties()[this.key].type === Models.PropertyTypes.ARRAY) {
      this.model[this.key] = this.arrayElements;
    }
  }

  redirectOnChange(event) {
    this.model[this.key] = event.value._d;
    this.change(event.value);
  }

  async openTimePicker() {
    let time = this.model[this.key] === undefined ? null : this.model[this.key];
    let dialogRef: MatDialogRef<TimePickerComponent>;
    dialogRef = this.dialog.open(TimePickerComponent, { width: '398px', height: '548px', panelClass: 'time', data: time });
    const result = await dialogRef.afterClosed().toPromise();
    if (!!result) {
      let hour = result.hour < 10 ? '0' + result.hour : (result.hour === 24 ? '00' : result.hour);
      let minute = result.minute < 10 ? '0' + result.minute : result.minute;
      this.model[this.key] = hour + ':' + minute;
      this.change(hour + ':' + minute);
    }
  }

  getFieldTitle() {
    return this.fieldConfig?.translations?.[this.currentLanguage]?.title || this.fieldConfig?.translations?.[this.currentLanguage] || this.translations?.[this.keyName] || ((typeof this.translations === 'string' && this.translationKey && !this.translationPrefix) ? this.translations : this.keyName);
  }

  getDropdownText(key) {
    return this.fieldConfig?.translations?.[this.currentLanguage]?.[key] || this.translations?.[key] || key;
  }

  clickColorPicker(key, event) {
    if (event.target !== document.getElementById('colorPicker' + key)) {
      document.getElementById('colorPicker' + key).click();
    }
  }
}
